import React, { useState, useEffect } from 'react';
import { Download, TrendingUp, DollarSign, Eye, MousePointer, Target } from 'lucide-react';
import apiClient from '../services/api';
import { toast } from 'sonner';

// Unmeasured metrics arrive as null (unknown). Show a dash, never 0 or a made-up number.
const fmt = (v, prefix = '', suffix = '') =>
  v === null || v === undefined ? '\u2014' : `${prefix}${typeof v === 'number' ? v.toLocaleString() : v}${suffix}`;

export default function PerformanceAnalytics() {
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [sortBy, setSortBy] = useState('revenue');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, sortBy]);

  const loadAnalytics = async () => {
    try {
      const response = await apiClient.get('/api/merchant/analytics/deals/performance', {
        params: { time_range: timeRange, sort_by: sortBy }
      });
      setDeals(response.data.deals || []);
      setSummary(response.data.summary || {});
      setLoading(false);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiClient.get('/api/merchant/analytics/export/csv', {
        params: { time_range: timeRange },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `deal_performance_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Performance data exported!', {
        description: 'CSV file downloaded'
      });
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-[#22304A] rounded w-1/3"></div>
          <div className="h-32 bg-[#22304A] rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-[#3EF1C7]" />
              <span className="text-xs text-[#9CB0C7]">Total Views</span>
            </div>
            <div className="text-2xl font-bold text-white">{fmt(summary.total_views)}</div>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-4">
            <div className="flex items-center gap-2 mb-2">
              <MousePointer className="w-4 h-4 text-[#7A5CFF]" />
              <span className="text-xs text-[#9CB0C7]">Total Clicks</span>
            </div>
            <div className="text-2xl font-bold text-white">{fmt(summary.total_clicks)}</div>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-4 h-4 text-[#8CF78E]" />
              <span className="text-xs text-[#9CB0C7]">Conversions</span>
            </div>
            <div className="text-2xl font-bold text-white">{fmt(summary.total_conversions)}</div>
          </div>
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-[#FFC857]" />
              <span className="text-xs text-[#9CB0C7]">Revenue</span>
            </div>
            <div className="text-2xl font-bold text-white">{fmt(summary.total_revenue, '$')}</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-4">
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-3 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60 text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60 text-sm"
            >
              <option value="revenue">Sort by Revenue</option>
              <option value="views">Sort by Views</option>
              <option value="conversions">Sort by Conversions</option>
            </select>
          </div>
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-[#3EF1C7] text-[#0B1120] rounded-lg hover:bg-[#3EF1C7]/90 transition-colors font-semibold text-sm flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Performance Table */}
      <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#111A2E]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Product</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">CI Score</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Views</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Clicks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">CTR</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Conv.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">ROI</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#9CB0C7] uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#22304A]">
              {deals.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-8 text-center text-[#9CB0C7]">
                    No deals found for this time range
                  </td>
                </tr>
              ) : (
                deals.map((deal) => (
                  <tr key={deal._id} className="hover:bg-[#111A2E]/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-white">{deal.product_title}</div>
                      <div className="text-xs text-[#9CB0C7]">{deal.product_category}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-bold border ${getCIScoreColor(deal.ci_score)}`}>
                        {deal.ci_score ?? '\u2014'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-white tabular-nums">{fmt(deal.views)}</td>
                    <td className="px-4 py-3 text-sm text-white tabular-nums">{fmt(deal.clicks)}</td>
                    <td className="px-4 py-3 text-sm text-white tabular-nums">{fmt(deal.click_through_rate, '', '%')}</td>
                    <td className="px-4 py-3 text-sm text-white tabular-nums">{fmt(deal.conversions)}</td>
                    <td className="px-4 py-3 text-sm text-[#8CF78E] font-semibold tabular-nums">{fmt(deal.revenue, '$')}</td>
                    <td className="px-4 py-3 text-sm tabular-nums">
                      <span className={(deal.roi ?? 0) >= 0 ? 'text-[#8CF78E]' : 'text-[#F55454]'}>
                        {fmt(deal.roi, '', '%')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${deal.status === 'active' ? 'bg-[#8CF78E]/10 text-[#8CF78E]' : 'bg-[#9CB0C7]/10 text-[#9CB0C7]'}`}>
                        {deal.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  function getCIScoreColor(score) {
    if (score >= 800) return 'text-[#3EF1C7] bg-[#3EF1C7]/10 border-[#3EF1C7]/20';
    if (score >= 600) return 'text-[#8CF78E] bg-[#8CF78E]/10 border-[#8CF78E]/20';
    if (score >= 300) return 'text-[#FFC857] bg-[#FFC857]/10 border-[#FFC857]/20';
    return 'text-[#F55454] bg-[#F55454]/10 border-[#F55454]/20';
  }
}

